/**
 * Валидатор контрольной суммы GS1 Modulo 10 (EAN-13, EAN-8, UPC-A, GTIN-14).
 * Стандарт: GS1 General Specifications.
 */
export function isValidEanChecksum(barcode: string): boolean {
  const clean = barcode.trim()
  if (!/^\d+$/.test(clean)) return false
  if (clean.length !== 8 && clean.length !== 12 && clean.length !== 13 && clean.length !== 14) {
    return false
  }
  const digits = clean.split('').map(Number)
  const checkDigit = digits[digits.length - 1]
  const dataDigits = digits.slice(0, -1)

  // Расчёт контрольной суммы: с конца к началу, чередуя веса 3 и 1.
  let sum = 0
  let weight = 3
  for (let i = dataDigits.length - 1; i >= 0; i--) {
    sum += dataDigits[i] * weight
    weight = weight === 3 ? 1 : 3
  }
  const calculatedCheckDigit = (10 - (sum % 10)) % 10
  return checkDigit === calculatedCheckDigit
}

export interface BarcodeValidationResult {
  isValid: boolean
  format: 'EAN-13' | 'EAN-8' | 'DATAMATRIX' | 'CODE-128' | 'CUSTOM' | 'INVALID'
  sanitizedCode: string
  error?: string
}

/**
 * Промышленный фильтр-санитайзер кодов:
 * - Отсекает недосканированные обрезки EAN-13 (9, 10, 11 цифр).
 * - Проверяет контрольную цифру EAN-13 и EAN-8.
 * - Пропускает длинные коды маркировки DataMatrix.
 */
export function validateScannedBarcode(raw: string): BarcodeValidationResult {
  const code = raw.trim()
  if (!code || code.length < 3) {
    return { isValid: false, format: 'INVALID', sanitizedCode: code, error: 'Слишком короткий код' }
  }

  // 1. DataMatrix «Честный Знак» / QR (длинные коды или спецсимволы GS1 FNC1).
  if (code.length >= 20 || code.includes('\u001d') || /^[0-9]{14}[a-zA-Z0-9]/.test(code)) {
    return { isValid: true, format: 'DATAMATRIX', sanitizedCode: code }
  }

  // 2. Цифровые товарные штрихкоды.
  if (/^\d+$/.test(code)) {
    // Отсекаем явные «обрезки» EAN-13.
    if (code.length === 9 || code.length === 10 || code.length === 11) {
      return { isValid: false, format: 'INVALID', sanitizedCode: code, error: 'Неполный скан штрихкода' }
    }
    // EAN-13.
    if (code.length === 13) {
      if (!isValidEanChecksum(code)) {
        return {
          isValid: false,
          format: 'INVALID',
          sanitizedCode: code,
          error: 'Ошибка контрольной суммы EAN-13',
        }
      }
      return { isValid: true, format: 'EAN-13', sanitizedCode: code }
    }
    // EAN-8.
    if (code.length === 8) {
      if (!isValidEanChecksum(code)) {
        return {
          isValid: false,
          format: 'INVALID',
          sanitizedCode: code,
          error: 'Ошибка контрольной суммы EAN-8',
        }
      }
      return { isValid: true, format: 'EAN-8', sanitizedCode: code }
    }
  }

  // 3. Другие буквенно-цифровые коды (Code-128).
  if (code.length >= 4) {
    return { isValid: true, format: 'CODE-128', sanitizedCode: code }
  }

  return { isValid: false, format: 'INVALID', sanitizedCode: code, error: 'Неизвестный формат кода' }
}
